import { useCallback, useEffect, useState } from "react";
import { ThumbsDown, ThumbsUp } from "@phosphor-icons/react";
import { ShimmerText } from "~/components/assistant/ShimmerText";
import {
  NEGATIVE_IMPACT_LABELS,
  POSITIVE_IMPACT_LABELS,
} from "~/components/assistant/impact-options";
import { cn } from "~/lib/utils";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import type { PartialAgent } from "~/services/agents";
import {
  deleteTurnFeedback,
  putTurnFeedback,
  type FeedbackCreate,
  type TurnFeedback as TurnFeedbackValue,
} from "~/services/turns";
import { DeferredToolFallbackCard } from "./DeferredToolFallbackCard";
import { Markdown } from "./Markdown";
import { useMcpAppEntries } from "./McpAppEntriesContext";
import { McpAppFrame } from "./McpAppFrame";
import { ToolCallsDisclosure } from "./ToolCallsDisclosure";
import { useToolUIMap } from "./ToolUIContext";
import { TurnFeedback } from "./TurnFeedback";
import { HITL_TOOL_RENDERERS, isHitlTool } from "./hitlTools";
import type { AssistantTurnData, ToolCallEntry } from "./useTurns";

interface AssistantTurnProps {
  turn: AssistantTurnData;
  agent: PartialAgent | null;
  conversationId: string;
  // True between clicking Stop and the run ending. Only meaningful on the
  // in-flight turn; swaps the status label from "Thinking" to "Stopping".
  isCancelling?: boolean;
  // Admin-review mode: replace the thumbs bar with a static tinted feedback
  // card and an avatar thread line. No-op if the turn has no persisted feedback.
  readOnlyFeedback?: boolean;
  // Submit a response to a paused human-in-the-loop tool call, resuming the
  // run. Omitted in read-only contexts (e.g. admin review).
  onAnswer?: (toolCallId: string, answer: string) => void | Promise<void>;
  // Tool-call ids genuinely paused awaiting human input. Only these render an
  // answerable card; empty while a run is in flight or being reconciled.
  awaitingToolCallIds?: ReadonlySet<string>;
}

const EMPTY_AWAITING: ReadonlySet<string> = new Set();

export const AssistantTurn = ({
  turn,
  agent,
  conversationId,
  isCancelling,
  readOnlyFeedback = false,
  onAnswer,
  awaitingToolCallIds = EMPTY_AWAITING,
}: AssistantTurnProps) => {
  const showThinking = turn.isRunning;
  const hasText = turn.text.length > 0;
  const mcpAppEntries = useMcpAppEntries();
  const toolUIMap = useToolUIMap();

  // HITL tools with a bespoke card always render it; a HITL tool without one
  // gets the generic answer box while it awaits input, so a backend HITL tool
  // can ship ahead of its card without bricking the run. Everything else —
  // including a non-HITL call left un-resulted by an interrupted run — stays
  // in the read-only pager.
  const isAwaiting = (tc: ToolCallEntry) => awaitingToolCallIds.has(tc.id);
  const hasCard = (tc: ToolCallEntry) => tc.name in HITL_TOOL_RENDERERS;
  const isFallback = (tc: ToolCallEntry) => isHitlTool(tc.name) && !hasCard(tc) && isAwaiting(tc);
  const hitlCalls = turn.toolCalls.filter(hasCard);
  const fallbackCalls = turn.toolCalls.filter(isFallback);
  const otherCalls = turn.toolCalls.filter((tc) => !hasCard(tc) && !isFallback(tc));
  const hasTools = otherCalls.length > 0;
  const mcpFrames = turn.toolCalls.flatMap((tc) => {
    if (toolUIMap.has(tc.name)) return [];
    const entry = mcpAppEntries.get(tc.id);
    return entry?.html ? [{ id: tc.id, entry }] : [];
  });
  const showCancelled = turn.cancelled && !turn.isRunning;
  const showInteractiveFeedback =
    !readOnlyFeedback && !turn.isRunning && !turn.cancelled && !!turn.turnId;
  const adminFeedback = readOnlyFeedback ? binaryFeedback(turn) : null;

  const [feedback, setFeedback] = useState<TurnFeedbackValue | FeedbackCreate | null>(
    turn.feedback ?? null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // True once we've seen this turn running this session. History turns mount
  // with isRunning=false, so `sawRunning` stays false and the feedback bar
  // appears without animation; live turns flip it true, so the bar animates in
  // when the run finishes.
  const [sawRunning, setSawRunning] = useState(false);
  useEffect(() => {
    if (turn.isRunning) setSawRunning(true);
  }, [turn.isRunning]);

  // Re-sync if /connect later returns a different feedback value for this
  // turn (e.g. the conversation reloaded after a navigation).
  useEffect(() => {
    setFeedback(turn.feedback ?? null);
  }, [turn.feedback]);

  const handleSubmit = useCallback(
    async (value: 0 | 1, text: string | null, impact: number | null) => {
      if (!turn.turnId || isSubmitting) return;
      setIsSubmitting(true);
      try {
        const payload: FeedbackCreate = {
          feedback_type: "BINARY",
          feedback_value: value,
          text,
          impact,
        };
        const result = await putTurnFeedback(conversationId, turn.turnId, payload);
        setFeedback(result);
      } catch (err) {
        console.error("Failed to submit turn feedback", err);
      } finally {
        setIsSubmitting(false);
      }
    },
    [conversationId, isSubmitting, turn.turnId]
  );

  const handleClear = useCallback(async () => {
    if (!turn.turnId || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await deleteTurnFeedback(conversationId, turn.turnId);
      setFeedback(null);
    } catch (err) {
      console.error("Failed to clear turn feedback", err);
    } finally {
      setIsSubmitting(false);
    }
  }, [conversationId, isSubmitting, turn.turnId]);

  return (
    <li className="flex w-full items-stretch gap-3 self-start">
      <div className="flex w-7 flex-col items-center">
        <AgentAvatar agent={agent} size={28} aria-hidden="true" />
        {adminFeedback && (
          <div
            className={cn(
              "mt-1 w-0.5 flex-1 rounded-full",
              adminFeedback.tone === "positive" ? "bg-emerald-500/60" : "bg-destructive/60"
            )}
          />
        )}
      </div>
      <div className="relative flex min-w-0 flex-1 flex-col gap-2">
        {(agent?.display_name || showThinking) && (
          <div className="absolute -top-4 left-1 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
            {agent?.display_name && <span>{agent.display_name}</span>}
            {showThinking && (
              <span className="inline-flex items-center gap-1.5" aria-live="polite">
                {agent?.display_name && <span aria-hidden>·</span>}
                <ShimmerText>{isCancelling ? "Stopping" : "Thinking"}</ShimmerText>
              </span>
            )}
          </div>
        )}
        {hasTools && <ToolCallsDisclosure entries={otherCalls} isRunning={turn.isRunning} />}
        {turn.toolCalls.map(({ id, name, args, result }) => {
          const UI = toolUIMap.get(name);
          if (!UI) return null;
          return (
            <UI key={id} toolCallId={id} args={args} result={result} isRunning={turn.isRunning} />
          );
        })}
        {hitlCalls.map((tc) => {
          // Always render the card (read-only when onAnswer is absent, as in
          // shared/admin views). Pass onAnswer only while the call is awaiting
          // input, so e.g. a cancelled turn can't resume a stopped run.
          const Renderer = HITL_TOOL_RENDERERS[tc.name];
          return (
            <Renderer
              key={tc.id}
              entry={tc}
              isRunning={turn.isRunning}
              onAnswer={isAwaiting(tc) ? onAnswer : undefined}
            />
          );
        })}
        {fallbackCalls.map((tc) => (
          <DeferredToolFallbackCard key={tc.id} entry={tc} onAnswer={onAnswer} />
        ))}
        {hasText && (
          <div className="px-1 py-1 text-sm text-foreground">
            <Markdown text={turn.text} />
          </div>
        )}
        {mcpFrames.map(({ id, entry }) => (
          <McpAppFrame
            key={id}
            html={entry.html!}
            title={entry.title}
            toolResult={entry.toolResult}
          />
        ))}
        {showCancelled && (
          <div className="px-1 text-xs italic text-muted-foreground" aria-live="polite">
            Response cancelled
          </div>
        )}
        {showInteractiveFeedback && (
          <div
            className={
              sawRunning
                ? "-ml-1 mt-0.5 animate-in fade-in slide-in-from-bottom-1 fill-mode-both duration-300 delay-150 ease-out"
                : "-ml-1 mt-0.5"
            }
          >
            <TurnFeedback
              feedback={feedback}
              isSubmitting={isSubmitting}
              onSubmit={handleSubmit}
              onClear={handleClear}
            />
          </div>
        )}
        {adminFeedback && <FeedbackCard feedback={adminFeedback} />}
      </div>
    </li>
  );
};

interface BinaryFeedback {
  tone: "positive" | "negative";
  impactLabel: string | null;
  text: string | null;
}

function binaryFeedback(turn: AssistantTurnData): BinaryFeedback | null {
  const fb = turn.feedback;
  if (!fb || fb.feedback_type !== "BINARY" || fb.feedback_value == null) return null;
  const isPositive = fb.feedback_value === 1;
  return {
    tone: isPositive ? "positive" : "negative",
    impactLabel: impactLabel(fb, isPositive),
    text: fb.text?.trim() || null,
  };
}

function impactLabel(fb: TurnFeedbackValue, isPositive: boolean): string | null {
  if (fb.impact == null) return null;
  const table = isPositive ? POSITIVE_IMPACT_LABELS : NEGATIVE_IMPACT_LABELS;
  return table[fb.impact] ?? null;
}

const FeedbackCard = ({ feedback }: { feedback: BinaryFeedback }) => {
  const isPositive = feedback.tone === "positive";
  return (
    <div
      className={cn(
        "flex flex-col gap-1 rounded-md border px-3 py-2 text-xs",
        isPositive
          ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-800"
          : "border-destructive/30 bg-destructive/[0.06] text-destructive"
      )}
    >
      <div className="flex items-center gap-1.5">
        {isPositive ? <ThumbsUp size={14} weight="fill" /> : <ThumbsDown size={14} weight="fill" />}
        <span className="font-medium">User feedback</span>
        {feedback.impactLabel && (
          <span className="font-normal opacity-80">· {feedback.impactLabel}</span>
        )}
      </div>
      {feedback.text && <div className="italic">&ldquo;{feedback.text}&rdquo;</div>}
    </div>
  );
};
